import { useState, useEffect, useCallback } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import LayerHeader from '../components/ui/LayerHeader';
import { NumCell } from '../components/ui/NumCell';
import { TH, makeSticky } from '../components/ui/gridTable';
import Dropdown from '../components/receptura/Dropdown';
import { num, fmtKc, MESICE_SHORT, MKEYS } from '../lib/formatters';
import { W_MESIC, tableSurfaces, ACCENT, OK, VIOLET, WARN, ENERGIE_BARVA } from '../lib/uiTokens';
import { AopTable, BtnUlozit, LayerSpinner } from '../components/ui/aopUi';
import useSirokyDisplej from '../hooks/useSirokyDisplej';

const DRUH_COLOR = ENERGIE_BARVA;
const DPH_COLOR = { 0: 'var(--text-tertiary)', 12: OK, 21: VIOLET };

const W = { druh: 128, dph: 66, spotreba: 150, cena: 116, fix: 116, rocni: 132, mesic: W_MESIC, lock: 46 };
const LEFT = {
    druh: 0, dph: W.druh, spotreba: W.druh + W.dph, cena: W.druh + W.dph + W.spotreba,
    fix: W.druh + W.dph + W.spotreba + W.cena, rocni: W.druh + W.dph + W.spotreba + W.cena + W.fix,
};

const miniNum = 'bg-transparent text-right text-[14px] rounded px-1 py-1 outline-none tabular-nums focus:ring-1 focus:ring-blue-500/60 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none';

// Rovnoměrný měsíční náklad = (spotřeba/rok × cena) / 12 + měsíční fix (platí se zálohově)
const evenMonthly = (r) => Math.round(((num(r.spotreba_rok) * num(r.cena_jednotka)) / 12 + num(r.mesicni_fix)) * 100) / 100;

export default function FinanceAopEnergie({ rok, variantaId, cardStyle, isDark }) {
    const siroky = useSirokyDisplej();
    const [rows, setRows] = useState([]);
    const [dirty, setDirty] = useState(() => new Set());
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const headers = { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token };
    const qVar = variantaId ? `&varianta_id=${variantaId}` : '';

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const d = await fetch(`/api/finance/aop/energie?rok=${rok}${qVar}`).then(r => r.json());
            setRows(d.radky || []);
            setDirty(new Set());
        } catch (e) { console.error(e); }
        setLoading(false);
    }, [rok, variantaId]);

    useEffect(() => { loadData(); }, [loadData]);

    const updateCell = (id, field, value) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const next = { ...r, [field]: value };
            if (/^m\d\d$/.test(field)) {
                next.zamceno = true; // ruční úprava měsíce → zámek proti přepočtu
            } else if (['spotreba_rok', 'cena_jednotka', 'mesicni_fix'].includes(field) && !next.zamceno) {
                const v = evenMonthly(next);
                MKEYS.forEach(k => { next[k] = v; });
            }
            return next;
        }));
        setDirty(prev => new Set(prev).add(id));
    };

    const toggleLock = (id) => {
        setRows(prev => prev.map(r => {
            if (r.id !== id) return r;
            const zamceno = !r.zamceno;
            const next = { ...r, zamceno };
            if (!zamceno) { const v = evenMonthly(next); MKEYS.forEach(k => { next[k] = v; }); }
            return next;
        }));
        setDirty(prev => new Set(prev).add(id));
    };

    const rowBody = (r) => ({
        nazev: r.nazev,
        jednotka: r.jednotka || '',
        spotreba_rok: num(r.spotreba_rok),
        cena_jednotka: num(r.cena_jednotka),
        mesicni_fix: num(r.mesicni_fix),
        dph_sazba: Number(r.dph_sazba),
        zamceno: !!r.zamceno,
        poznamka: r.poznamka || null,
        ...Object.fromEntries(MKEYS.map(k => [k, num(r[k])])),
    });

    const handleUlozit = async () => {
        if (dirty.size === 0) return;
        setSaving(true);
        try {
            await Promise.all([...dirty].map(id => {
                const r = rows.find(x => x.id === id);
                return r ? fetch(`/api/finance/aop/energie/${id}`, { method: 'PUT', headers, body: JSON.stringify(rowBody(r)) }) : null;
            }));
            loadData();
        } catch (e) { alert('Chyba při ukládání'); }
        setSaving(false);
    };

    if (loading) {
        return <LayerSpinner />;
    }

    const rocni = (r) => MKEYS.reduce((s, k) => s + num(r[k]), 0);
    const energieM = MKEYS.map(k => rows.reduce((s, r) => s + num(r[k]), 0));
    const energieRok = energieM.reduce((a, b) => a + b, 0);
    const dph12M = MKEYS.map(k => rows.filter(r => Number(r.dph_sazba) === 12).reduce((s, r) => s + num(r[k]) * 0.12, 0));
    const dph21M = MKEYS.map(k => rows.filter(r => Number(r.dph_sazba) === 21).reduce((s, r) => s + num(r[k]) * 0.21, 0));
    const dph12Rok = dph12M.reduce((a, b) => a + b, 0);
    const dph21Rok = dph21M.reduce((a, b) => a + b, 0);
    const sDphRok = energieRok + dph12Rok + dph21Rok;

    const { stickyBg, headBg } = tableSurfaces(isDark);
    const inp = { color: 'var(--text-primary)' };
    const th = TH;
    const sticky = makeSticky(LEFT, 'rocni', siroky ? null : ['druh']);

    return (
        <div className="flex flex-col flex-1 min-h-0">
            {/* Hlavička vrstvy */}
            <LayerHeader krok={4} title="Energie" cardStyle={cardStyle}>
                Voda / Plyn / Elektřina. Zadej <b>roční spotřebu</b> (v jednotce), <b>cenu za jednotku</b> a <b>měsíční fix</b> —
                měsíc = (spotřeba × cena) / 12 + fix, tedy <b>rovnoměrný rozpad</b> (platíme zálohově). Ruční úprava měsíce řádek
                zamkne (přepočet ho přeskočí). Ceny bez DPH; DPH sazba slouží pro souhrn DPH na vstupu.
            </LayerHeader>

            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap flex-shrink-0">
                <div className="flex-1" />
                <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    Energie celkem: <b style={{ color: 'var(--text-primary)' }}>{fmtKc(energieRok)}</b> / rok (bez DPH)
                </div>
                <BtnUlozit onClick={handleUlozit} saving={saving} dirtyCount={dirty.size} />
            </div>

            {/* Tabulka energií */}
            <AopTable cardStyle={cardStyle}>
                        <colgroup>
                            <col style={{ width: W.druh }} />
                            <col style={{ width: W.dph }} />
                            <col style={{ width: W.spotreba }} />
                            <col style={{ width: W.cena }} />
                            <col style={{ width: W.fix }} />
                            <col style={{ width: W.rocni }} />
                            {MKEYS.map(k => <col key={k} style={{ width: W.mesic }} />)}
                            <col style={{ width: W.lock }} />
                        </colgroup>
                        <thead>
                            <tr style={{ borderBottom: '1px solid var(--border-default)' }}>
                                <th className={`${th} text-left`} style={{ ...sticky('druh', 30, headBg), color: 'var(--text-tertiary)' }}>Druh</th>
                                <th className={`${th} text-center`} style={{ ...sticky('dph', 30, headBg), color: 'var(--text-tertiary)' }}>DPH</th>
                                <th className={`${th} text-right`} style={{ ...sticky('spotreba', 30, headBg), color: 'var(--text-tertiary)' }}>Spotřeba/rok</th>
                                <th className={`${th} text-right`} style={{ ...sticky('cena', 30, headBg), color: 'var(--text-tertiary)' }}>Cena/jed.</th>
                                <th className={`${th} text-right`} style={{ ...sticky('fix', 30, headBg), color: 'var(--text-tertiary)' }}>Měs. fix</th>
                                <th className={`${th} text-right`} style={{ ...sticky('rocni', 30, headBg), color: ACCENT }}>Roční</th>
                                {MESICE_SHORT.map((m, i) => <th key={i} className={`${th} text-right`} style={{ color: 'var(--text-tertiary)', background: headBg }}>{m}</th>)}
                                <th className={th} style={{ background: headBg }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => {
                                const kColor = DRUH_COLOR[r.kanal_kod] || '#888';
                                return (
                                    <tr key={r.id}>
                                        <td className="px-2 py-1.5" style={{ ...sticky('druh', 10, stickyBg) }}>
                                            <span style={{ fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: kColor + '26', color: kColor, whiteSpace: 'nowrap' }}>{r.nazev}</span>
                                        </td>
                                        <td className="px-1 py-1.5 text-center" style={{ ...sticky('dph', 10, stickyBg) }}>
                                            <Dropdown value={Number(r.dph_sazba)} onChange={v => updateCell(r.id, 'dph_sazba', Number(v))}
                                                options={[{ value: 0, label: '0 %' }, { value: 12, label: '12 %' }, { value: 21, label: '21 %' }]}
                                                isDark={isDark} accent="#3B82F6" height={28} />
                                        </td>
                                        <td className="px-1 py-1.5" style={{ ...sticky('spotreba', 10, stickyBg) }}>
                                            <div className="flex items-center justify-end gap-1">
                                                <input type="text" inputMode="decimal" value={r.spotreba_rok ?? ''} placeholder="0"
                                                    onChange={e => updateCell(r.id, 'spotreba_rok', e.target.value.replace(/[^\d.]/g, ''))}
                                                    className={miniNum + ' w-20'} style={inp} />
                                                <input type="text" value={r.jednotka ?? ''} placeholder="j."
                                                    onChange={e => updateCell(r.id, 'jednotka', e.target.value)}
                                                    className="bg-transparent text-left text-[12px] rounded px-1 py-1 outline-none w-9 focus:ring-1 focus:ring-blue-500/60"
                                                    style={{ color: 'var(--text-tertiary)' }} />
                                            </div>
                                        </td>
                                        <td className="px-1 py-1.5" style={{ ...sticky('cena', 10, stickyBg) }}>
                                            <div className="flex items-center justify-end gap-1">
                                                <input type="text" inputMode="decimal" value={r.cena_jednotka ?? ''} placeholder="0"
                                                    onChange={e => updateCell(r.id, 'cena_jednotka', e.target.value.replace(/[^\d.]/g, ''))}
                                                    className={miniNum + ' w-16'} style={inp} />
                                                <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Kč</span>
                                            </div>
                                        </td>
                                        <td className="px-1 py-1.5" style={{ ...sticky('fix', 10, stickyBg) }}>
                                            <div className="flex items-center justify-end gap-1">
                                                <input type="text" inputMode="decimal" value={r.mesicni_fix ?? ''} placeholder="0"
                                                    onChange={e => updateCell(r.id, 'mesicni_fix', e.target.value.replace(/[^\d.]/g, ''))}
                                                    className={miniNum + ' w-16'} style={inp} />
                                                <span className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Kč</span>
                                            </div>
                                        </td>
                                        <td className="px-2 py-1.5 text-right text-[14px] font-mono font-semibold tabular-nums" style={{ ...sticky('rocni', 10, stickyBg), color: r.zamceno ? WARN : 'var(--text-primary)' }}>{fmtKc(rocni(r))}</td>
                                        {MKEYS.map(k => (
                                            <td key={k}><NumCell value={r[k]} onChange={v => updateCell(r.id, k, v)} color="var(--text-primary)" kc sm /></td>
                                        ))}
                                        <td className="text-center">
                                            <button onClick={() => toggleLock(r.id)} title={r.zamceno ? 'Odemknout (povolit přepočet)' : 'Zamknout (ruční hodnoty)'} className="opacity-70 hover:opacity-100">
                                                {r.zamceno ? <Lock size={13} style={{ color: WARN }} /> : <LockOpen size={13} style={{ color: 'var(--text-tertiary)' }} />}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr style={{ borderTop: '2px solid var(--border-default)', background: headBg }}>
                                <td className="px-2 py-2.5 text-[15px] font-bold" style={{ ...sticky('druh', 20, headBg), color: 'var(--text-primary)' }} colSpan={5}>Energie bez DPH</td>
                                <td className="px-2 py-2.5 text-right text-[14px] font-mono font-bold tabular-nums" style={{ ...sticky('rocni', 20, headBg), color: 'var(--text-primary)' }}>{fmtKc(energieRok)}</td>
                                {energieM.map((s, i) => <td key={i} className="px-2 py-2.5 text-right text-[13px] font-mono tabular-nums" style={{ color: 'var(--text-secondary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td style={{ background: headBg }}></td>
                            </tr>
                            <tr style={{ background: headBg }}>
                                <td className="px-2 py-1.5 text-[14px]" style={{ ...sticky('druh', 20, headBg), color: OK }} colSpan={5}>DPH 12 %</td>
                                <td className="px-2 py-1.5 text-right text-[13px] font-mono tabular-nums" style={{ ...sticky('rocni', 20, headBg), color: OK }}>{fmtKc(dph12Rok)}</td>
                                {dph12M.map((s, i) => <td key={i} className="px-2 py-1.5 text-right text-[12px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td style={{ background: headBg }}></td>
                            </tr>
                            <tr style={{ background: headBg }}>
                                <td className="px-2 py-1.5 text-[14px]" style={{ ...sticky('druh', 20, headBg), color: VIOLET }} colSpan={5}>DPH 21 %</td>
                                <td className="px-2 py-1.5 text-right text-[13px] font-mono tabular-nums" style={{ ...sticky('rocni', 20, headBg), color: VIOLET }}>{fmtKc(dph21Rok)}</td>
                                {dph21M.map((s, i) => <td key={i} className="px-2 py-1.5 text-right text-[12px] font-mono tabular-nums" style={{ color: 'var(--text-tertiary)', background: headBg }}>{fmtKc(s)}</td>)}
                                <td style={{ background: headBg }}></td>
                            </tr>
                            <tr style={{ borderTop: '1px solid var(--border-default)', background: headBg }}>
                                <td className="px-2 py-2.5 text-[15px] font-bold" style={{ ...sticky('druh', 20, headBg), color: 'var(--text-primary)' }} colSpan={5}>Energie s DPH</td>
                                <td className="px-2 py-2.5 text-right text-[14px] font-mono font-bold tabular-nums" style={{ ...sticky('rocni', 20, headBg), color: 'var(--text-primary)' }}>{fmtKc(sDphRok)}</td>
                                {energieM.map((n, i) => <td key={i} className="px-2 py-2.5 text-right text-[13px] font-mono font-semibold tabular-nums" style={{ color: 'var(--text-primary)', background: headBg }}>{fmtKc(n + dph12M[i] + dph21M[i])}</td>)}
                                <td style={{ background: headBg }}></td>
                            </tr>
                        </tfoot>
                    </AopTable>
        </div>
    );
}
